import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';
import { useToast } from '../hooks/use-toast';
import { staffelsAPI } from '../services/api';
import { 
  Percent, 
  Plus, 
  Pencil, 
  Trash2, 
  Package, 
  Users,
  Calendar,
  Hash,
  Loader2
} from 'lucide-react';

const StaffelManagement = () => {
  const [staffels, setStaffels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStaffel, setEditingStaffel] = useState(null);
  const [activeTab, setActiveTab] = useState('equipment');
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    type: 'equipment',
    basis: 'days',
    name: '',
    description: '',
    tiers: [],
    is_active: true
  });

  const [newTier, setNewTier] = useState({
    min_value: 1,
    max_value: '',
    discount_percentage: 0
  });

  useEffect(() => {
    fetchStaffels();
  }, []);

  const fetchStaffels = async () => {
    try {
      setLoading(true);
      const response = await staffelsAPI.getAll();
      setStaffels(response.data);
    } catch (error) {
      console.error('Fout bij ophalen staffels:', error);
      toast({
        title: "Fout",
        description: "Kan staffels niet ophalen",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = (type) => {
    setEditingStaffel(null);
    setFormData({
      type: type,
      basis: 'days',
      name: '',
      description: '',
      tiers: [],
      is_active: true
    });
    setNewTier({ min_value: 1, max_value: '', discount_percentage: 0 });
    setDialogOpen(true);
  };

  const openEditDialog = (staffel) => {
    setEditingStaffel(staffel);
    setFormData({
      type: staffel.type,
      basis: staffel.basis,
      name: staffel.name,
      description: staffel.description || '',
      tiers: staffel.tiers || [],
      is_active: staffel.is_active
    });
    setNewTier({ min_value: 1, max_value: '', discount_percentage: 0 });
    setDialogOpen(true);
  };

  const addTier = () => {
    if (newTier.discount_percentage < 0 || newTier.discount_percentage > 100) {
      toast({
        title: "Fout",
        description: "Korting moet tussen 0 en 100% zijn",
        variant: "destructive"
      });
      return;
    }

    const tier = {
      id: `tier-${Date.now()}`,
      min_value: parseInt(newTier.min_value),
      max_value: newTier.max_value ? parseInt(newTier.max_value) : null,
      discount_percentage: parseFloat(newTier.discount_percentage)
    };

    setFormData(prev => ({
      ...prev,
      tiers: [...prev.tiers, tier].sort((a, b) => a.min_value - b.min_value)
    }));

    setNewTier({ 
      min_value: tier.max_value ? tier.max_value + 1 : tier.min_value + 1, 
      max_value: '', 
      discount_percentage: 0 
    });
  };

  const removeTier = (tierId) => {
    setFormData(prev => ({
      ...prev,
      tiers: prev.tiers.filter(t => t.id !== tierId)
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast({
        title: "Fout",
        description: "Vul een naam in",
        variant: "destructive"
      });
      return;
    }

    if (formData.tiers.length === 0) {
      toast({
        title: "Fout",
        description: "Voeg minimaal één staffel niveau toe",
        variant: "destructive"
      });
      return;
    }

    try {
      if (editingStaffel) {
        await staffelsAPI.update(editingStaffel.id, formData);
        toast({
          title: "Gelukt",
          description: "Staffel succesvol bijgewerkt"
        });
      } else {
        await staffelsAPI.create(formData);
        toast({
          title: "Gelukt",
          description: "Staffel succesvol aangemaakt"
        });
      }
      setDialogOpen(false);
      fetchStaffels();
    } catch (error) {
      console.error('Fout bij opslaan staffel:', error);
      toast({
        title: "Fout",
        description: "Kan staffel niet opslaan",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (staffelId) => {
    try {
      await staffelsAPI.delete(staffelId);
      toast({
        title: "Gelukt",
        description: "Staffel succesvol verwijderd"
      });
      fetchStaffels();
    } catch (error) {
      console.error('Fout bij verwijderen staffel:', error);
      toast({
        title: "Fout",
        description: "Kan staffel niet verwijderen",
        variant: "destructive"
      });
    }
  };

  const toggleStaffelActive = async (staffel) => {
    try {
      await staffelsAPI.update(staffel.id, { is_active: !staffel.is_active });
      toast({
        title: "Gelukt",
        description: `Staffel ${!staffel.is_active ? 'geactiveerd' : 'gedeactiveerd'}`
      });
      fetchStaffels();
    } catch (error) {
      toast({
        title: "Fout",
        description: "Kan status niet wijzigen",
        variant: "destructive"
      });
    }
  };

  const getBasisLabel = (basis) => {
    return basis === 'days' ? 'Dagen' : 'Aantal';
  };

  const getBasisIcon = (basis) => {
    return basis === 'days' ? <Calendar className="w-4 h-4" /> : <Hash className="w-4 h-4" />;
  };

  const equipmentStaffels = staffels.filter(s => s.type === 'equipment');
  const crewStaffels = staffels.filter(s => s.type === 'crew');

  const renderStaffelTable = (staffelList, type) => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500">
          {type === 'equipment' 
            ? 'Configureer kortingen voor apparatuurverhuur op basis van huurdagen of aantal stuks.'
            : 'Configureer kortingen voor personeelsinhuur op basis van dagen of aantal personen.'}
        </p>
        <Button onClick={() => openCreateDialog(type)} className="bg-gradient-to-r from-blue-600 to-purple-600">
          <Plus className="w-4 h-4 mr-2" />
          Nieuwe Staffel
        </Button>
      </div>

      {staffelList.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <Percent className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500">Nog geen staffels geconfigureerd</p>
          <p className="text-sm text-gray-400">Maak een nieuwe staffel aan om kortingen te configureren</p>
        </div>
      ) : (
        <div className="space-y-4">
          {staffelList.map(staffel => (
            <Card key={staffel.id} className={`shadow-md border-0 ${!staffel.is_active ? 'opacity-60' : ''}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <CardTitle className="text-lg">{staffel.name}</CardTitle>
                    <Badge className="bg-blue-100 text-blue-700">
                      {getBasisIcon(staffel.basis)}
                      <span className="ml-1">{getBasisLabel(staffel.basis)}</span>
                    </Badge>
                    {!staffel.is_active && (
                      <Badge variant="outline" className="text-gray-500">Inactief</Badge>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={staffel.is_active}
                      onCheckedChange={() => toggleStaffelActive(staffel)}
                    />
                    <Button variant="outline" size="sm" onClick={() => openEditDialog(staffel)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Staffel Verwijderen</AlertDialogTitle>
                          <AlertDialogDescription>
                            Weet je zeker dat je de staffel "{staffel.name}" wilt verwijderen?
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Annuleren</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDelete(staffel.id)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Verwijderen
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
                {staffel.description && (
                  <p className="text-sm text-gray-500 mt-1">{staffel.description}</p>
                )}
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-semibold">
                        {staffel.basis === 'days' ? 'Dagen' : 'Aantal'}
                      </TableHead>
                      <TableHead className="font-semibold">Korting</TableHead>
                      <TableHead className="font-semibold">Voorbeeld (€100 basis)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {staffel.tiers.map((tier, idx) => (
                      <TableRow key={tier.id || idx}>
                        <TableCell>
                          {tier.max_value 
                            ? `${tier.min_value} - ${tier.max_value}`
                            : `${tier.min_value}+`}
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-green-100 text-green-700">
                            {tier.discount_percentage}%
                          </Badge>
                        </TableCell>
                        <TableCell className="text-gray-600">
                          €{(100 - (100 * tier.discount_percentage / 100)).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 lg:w-[400px]">
          <TabsTrigger value="equipment" className="flex items-center">
            <Package className="w-4 h-4 mr-2" />
            Apparatuur ({equipmentStaffels.length})
          </TabsTrigger>
          <TabsTrigger value="crew" className="flex items-center">
            <Users className="w-4 h-4 mr-2" />
            Personeel ({crewStaffels.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="equipment" className="mt-6">
          {renderStaffelTable(equipmentStaffels, 'equipment')}
        </TabsContent>

        <TabsContent value="crew" className="mt-6">
          {renderStaffelTable(crewStaffels, 'crew')}
        </TabsContent>
      </Tabs>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Percent className="w-5 h-5 mr-2 text-blue-600" />
              {editingStaffel ? 'Staffel Bewerken' : 'Nieuwe Staffel'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Naam</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Bijv. Weekkorting Apparatuur"
                />
              </div>
              <div className="space-y-2">
                <Label>Basis</Label>
                <Select 
                  value={formData.basis} 
                  onValueChange={(value) => setFormData({ ...formData, basis: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="days">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-2" />
                        Dagen (huurduur)
                      </div>
                    </SelectItem>
                    <SelectItem value="quantity">
                      <div className="flex items-center">
                        <Hash className="w-4 h-4 mr-2" />
                        Aantal (stuks/personen)
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Beschrijving (optioneel)</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Korte beschrijving van deze staffel"
              />
            </div>

            {/* Tiers */}
            <div className="space-y-4">
              <Label className="text-base font-semibold">Staffel Niveaus</Label>
              
              {/* Existing Tiers */}
              {formData.tiers.length > 0 && (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Van</TableHead>
                      <TableHead>Tot</TableHead>
                      <TableHead>Korting %</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {formData.tiers.map((tier) => (
                      <TableRow key={tier.id}>
                        <TableCell>{tier.min_value}</TableCell>
                        <TableCell>{tier.max_value || '∞'}</TableCell>
                        <TableCell>
                          <Badge className="bg-green-100 text-green-700">{tier.discount_percentage}%</Badge>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeTier(tier.id)}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              {/* Add New Tier */}
              <div className="p-4 bg-gray-50 rounded-lg space-y-4">
                <p className="text-sm font-medium text-gray-700">Nieuw niveau toevoegen</p>
                <div className="grid grid-cols-4 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Van</Label>
                    <Input
                      type="number"
                      min="1"
                      value={newTier.min_value}
                      onChange={(e) => setNewTier({ ...newTier, min_value: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Tot (leeg = onbeperkt)</Label>
                    <Input
                      type="number"
                      min={newTier.min_value}
                      value={newTier.max_value}
                      onChange={(e) => setNewTier({ ...newTier, max_value: e.target.value })}
                      placeholder="∞"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Korting %</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={newTier.discount_percentage}
                      onChange={(e) => setNewTier({ ...newTier, discount_percentage: e.target.value })}
                    />
                  </div>
                  <div className="flex items-end">
                    <Button onClick={addTier} className="w-full">
                      <Plus className="w-4 h-4 mr-1" />
                      Toevoegen
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Active Toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium">Staffel Actief</p>
                <p className="text-sm text-gray-500">Inactieve staffels worden niet toegepast</p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annuleren
            </Button>
            <Button onClick={handleSubmit} className="bg-gradient-to-r from-blue-600 to-purple-600">
              {editingStaffel ? 'Bijwerken' : 'Aanmaken'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffelManagement;
